import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/04052a67-6be6-4ee5-b8f8-93fa74602fd6/images/b0f80c64bf5dde30041c4f458580d7d2cdd5c054a6110b817a85ba9becb588b8.png";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        toast.success("If that email is registered, a reset link is on its way.");
        setMode("login");
      } else if (mode === "login") {
        await login(email, password);
        navigate("/");
      } else {
        await register(email, password, name);
        navigate("/");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden" style={{ backgroundColor: "#0F172A" }}>
        <div className="absolute inset-0 grain opacity-60" />
        <img
          src="https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBub2Rlc3xlbnwwfHx8fDE3ODI4NjQxOTh8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-20"
        />
        <div className="relative z-10">
          <img src={LOGO} alt="FMiQ" className="h-12 w-auto" />
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-5xl font-black tracking-tighter leading-[0.95]">
            Compare two datasets with confidence.
          </h1>
          <p className="mt-6 text-base text-white/70 leading-relaxed">
            Map columns across mismatched headers, compare two datasets with tolerance and
            fuzzy matching, reconcile records in both directions, and export defensible
            results with an AI-authored report.
          </p>
        </div>
        <div className="relative z-10 font-mono text-xs uppercase tracking-[0.3em] text-white/50">
          CSV · XLSX · PDF · DOCX
        </div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 inline-flex px-3 py-2 rounded-md" style={{ backgroundColor: "#0F172A" }}>
            <img src={LOGO} alt="FMiQ" className="h-7 w-auto" />
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">
            {mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Reset password"}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {mode === "login"
              ? "Access your comparison workspace."
              : mode === "register"
              ? "Start comparing datasets."
              : "Enter your email and we'll send you a link to set a new password."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Analyst"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            {mode !== "forgot" && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === "login" && (
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => { setMode("forgot"); setError(""); }}
                    className="text-xs text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            )}

            {error && (
              <div
                data-testid="auth-error"
                className="border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-button"
              className="w-full h-11 gap-2 group"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Button>
          </form>

          {mode === "forgot" ? (
            <button
              data-testid="back-to-signin"
              onClick={() => { setMode("login"); setError(""); }}
              className="mt-6 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              ← Back to sign in
            </button>
          ) : (
            <button
              data-testid="toggle-auth-mode"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="mt-6 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {mode === "login" ? "No account? Create one" : "Have an account? Sign in"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
